import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./ProductGallery.module.css";
import ItemImage from "Shared/ItemImage";

const SWIPE_THRESHOLD_PX = 40;
const WHEEL_DELTA_THRESHOLD = 10;
const WHEEL_LOCK_MS = 400;

/**
 * Full-size product image gallery for the detail page. Renders a single
 * main image; when there is more than one, clicking the left/right edge of
 * the image, or swiping/dragging with touch or mouse, slides to the
 * previous/next image, and a plain grey solid-line progress bar below shows
 * position - no arrows, no thumbnail previews. The caller supplies the
 * images for the selected color and remounts the gallery (via key) when
 * the color changes.
 */
export function ProductGallery({ images, title }) {
  const gallery = Array.isArray(images) ? images.filter(Boolean) : [];
  const [activeIndex, setActiveIndex] = useState(0);
  const dragStartXRef = useRef(null);
  const isMountedRef = useRef(true);
  const mainImageWrapRef = useRef(null);
  const wheelLockRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [gallery.length, title]);

  // React registers wheel listeners as passive, so preventDefault() has no
  // effect through the onWheel prop. Attach a native listener directly to
  // the image element (not the outer wrap, which also holds the progress
  // bar) so scrolling only cycles images when the cursor is over the image
  // itself, and page scroll is blocked only in that moment.
  useEffect(() => {
    const node = mainImageWrapRef.current;
    if (!node || gallery.length <= 1) return undefined;

    const handleWheel = (event) => {
      if (Math.abs(event.deltaY) < WHEEL_DELTA_THRESHOLD) return;
      event.preventDefault();
      if (wheelLockRef.current) return;
      wheelLockRef.current = true;
      setActiveIndex((current) => {
        const nextIndex = event.deltaY > 0 ? current + 1 : current - 1;
        return ((nextIndex % gallery.length) + gallery.length) % gallery.length;
      });
      setTimeout(() => {
        wheelLockRef.current = false;
      }, WHEEL_LOCK_MS);
    };

    node.addEventListener("wheel", handleWheel, { passive: false });
    return () => node.removeEventListener("wheel", handleWheel);
  }, [gallery.length]);

  if (gallery.length === 0) {
    return (
      <div className={styles.mainImageWrap}>
        <ItemImage imageSrc="" alt={title} />
      </div>
    );
  }

  const hasMultiple = gallery.length > 1;

  const goTo = (index) => {
    setActiveIndex(
      ((index % gallery.length) + gallery.length) % gallery.length
    );
  };

  // Left half of the wrapper goes to the previous image, right half to the
  // next - no visible arrows.
  const handleWrapClick = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    goTo(clickX < rect.width / 2 ? activeIndex - 1 : activeIndex + 1);
  };

  const resolveSwipe = (deltaX, baseIndex) => {
    if (deltaX > SWIPE_THRESHOLD_PX) {
      goTo(baseIndex - 1);
    } else if (deltaX < -SWIPE_THRESHOLD_PX) {
      goTo(baseIndex + 1);
    }
  };

  const handleTouchStart = (event) => {
    dragStartXRef.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    if (dragStartXRef.current == null) return;
    const endX = event.changedTouches[0]?.clientX ?? dragStartXRef.current;
    const deltaX = endX - dragStartXRef.current;
    dragStartXRef.current = null;
    resolveSwipe(deltaX, activeIndex);
  };

  // Mouse-drag swipe support mirrors touch: track the button-down start
  // position, then resolve the direction on mouseup anywhere on the page
  // (the drag can end outside the wrapper).
  const handleMouseDown = (event) => {
    const startX = event.clientX;
    const startIndex = activeIndex;

    const handleWindowMouseUp = (upEvent) => {
      window.removeEventListener("mouseup", handleWindowMouseUp);
      if (!isMountedRef.current) return;
      resolveSwipe(upEvent.clientX - startX, startIndex);
    };

    window.addEventListener("mouseup", handleWindowMouseUp);
  };

  return (
    <div className={styles.galleryWrap}>
      <div
        ref={mainImageWrapRef}
        className={styles.mainImageWrap}
        onClick={hasMultiple ? handleWrapClick : undefined}
        onTouchStart={hasMultiple ? handleTouchStart : undefined}
        onTouchEnd={hasMultiple ? handleTouchEnd : undefined}
        onMouseDown={hasMultiple ? handleMouseDown : undefined}
        onDragStart={(event) => event.preventDefault()}
        role={hasMultiple ? "button" : undefined}
        tabIndex={hasMultiple ? 0 : undefined}
        aria-label={
          hasMultiple
            ? `Image ${activeIndex + 1} of ${
                gallery.length
              }. Click the left or right edge, or swipe, to view other images.`
            : undefined
        }
      >
        <div
          className={styles.slideTrack}
          data-testid="slide-track"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {gallery.map((image, index) => {
            const src = typeof image === "string" ? image : image?.url;
            const key = (typeof image === "object" && image?.assetId) || index;
            return (
              <div className={styles.slide} key={key}>
                <ItemImage imageSrc={src} alt={title} />
              </div>
            );
          })}
        </div>
      </div>

      {hasMultiple && (
        <div
          className={styles.progressBar}
          role="tablist"
          aria-label="Image position"
        >
          {gallery.map((image, index) => {
            const key = (typeof image === "object" && image?.assetId) || index;
            return (
              <span
                key={key}
                className={`${styles.progressSegment} ${
                  index === activeIndex ? styles.progressSegmentActive : ""
                }`}
                role="tab"
                aria-selected={index === activeIndex}
                aria-label={`Image ${index + 1} of ${gallery.length}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

ProductGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        url: PropTypes.string,
        sortOrder: PropTypes.number,
      }),
    ])
  ),
  title: PropTypes.string,
};

ProductGallery.defaultProps = {
  images: [],
  title: "",
};
